import React, { useEffect, useState } from "react";

import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";

import { AddOutlined, FileDownloadOutlined } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    Collapse,
    Container,
    Fab,
    IconButton,
    InputBase,
    Snackbar,
} from "@mui/material";
import dayjs from "dayjs";
import { filter } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { monthNames } from "../../components/Calendar";
import { FilterContext, useFilter } from "../../components/Filter";
import {
    ListDetailViewSectionContent,
    ListDetailViewSectionHeader,
    ListDetailViewSectionLayout,
    ListViewItem,
    useListDetailContext,
} from "../../components/ListDetailView";
import {
    ListView,
    MainPage,
    MainView,
    PageContext,
    usePageState,
} from "../../components/PageLayout";
import {
    formatPeriod,
    initPeriod,
    isInPeriod,
    PeriodField,
    PeriodSelector,
} from "../../components/Period";
import { useSideSheet } from "../../components/SideSheet";
import { useUserInfo } from "../../utils/security";
import { TimesheetModal } from "./TimesheetModal";
import { TimesheetOverview } from "./TimesheetOverview";
import { fetchDetail, fetchList, fromMonthId, storeTimesheet, toMonthId } from "./mock";

export const TimesheetsIcon = EventAvailableOutlinedIcon;

const TimesheetDetail = ({ detailId, compact, loading, data, showModal }) => {
    const { year, month } = fromMonthId(detailId);

    return (
        <ListDetailViewSectionLayout loading={loading}>
            <ListDetailViewSectionHeader
                title={`${monthNames[month]} ${year}`}
                role="detail"
                loading={loading}
                compact={compact}
                actions={[
                    { title: "Export", icon: <FileDownloadOutlined /> },
                    {
                        title: "Přidat položku výkazu...",
                        icon: <AddOutlined />,
                        onClick: () => showModal?.(),
                    },
                ]}
            />
            <ListDetailViewSectionContent>
                <Container maxWidth="xl">
                    <Box sx={{ py: 4 }}>
                        <TimesheetOverview data={data} />
                    </Box>
                </Container>
            </ListDetailViewSectionContent>
        </ListDetailViewSectionLayout>
    );
};

const ListFilter = ({ setFilter, filter, compact }) => {
    const [state, setState] = useState(filter || {});
    const [filterVisible, setFilterVisible] = useState(false);
    //useEffect(() => setFilter(state), [state, setFilter]);

    const updateFilter = (newFilter) => {
        setState(newFilter);
        setFilter({ ...filter, ...newFilter });
    };

    return (
        <Box
            sx={{
                mt: 1,
                mb: 3,
                flex: "0 0 auto",
                px: compact ? 2 : 0,
            }}>
            <Box
                sx={(theme) => ({
                    display: "flex",
                    flexDirection: "row",
                    gap: theme.spacing(2),
                    alignItems: "center",
                    borderRadius: theme.spacing(3),
                    paddingTop: theme.spacing(0.5),
                    paddingRight: theme.spacing(1),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    backgroundColor:
                        (theme.palette.surfaceDim && theme.palette.surfaceDim.main) ||
                        theme.palette.grey["400"],
                })}>
                <SearchIcon />
                <InputBase
                    value={state.fulltext || ""}
                    onChange={(e) => updateFilter({ ...state, fulltext: e.target.value })}
                    placeholder="Filtrovat..."
                    sx={{ flex: 1 }}
                />
                <IconButton onClick={() => setFilterVisible(!filterVisible)} sx={{ flex: 0 }}>
                    <TuneIcon />
                </IconButton>
            </Box>

            <Collapse in={filterVisible}>
                <Box sx={{ height: 150 }}></Box>
            </Collapse>
        </Box>
    );
};

const TimesheetListItem = ({ data, loading, ...props }) => {
    return (
        <ListViewItem
            {...props}
            mainText={data.name}
            mainValue={loading ? <CircularProgress size={14} /> : `${data.hours || 0} h`}
        />
    );
};

export const TimesheetsPage = ({ pagePath }) => {
    const navigate = useNavigate();
    const { id: detailId } = useParams();
    const [snackbar, showSnackbar] = useState(false);
    const sideSheet = useSideSheet();
    const { userInfo } = useUserInfo();

    const filterState = useFilter({
        filter: {
            listPeriod: initPeriod("year"),
            detailPeriod: initPeriod("month"),
        },
    });

    const pageState = usePageState();
    const { state, setListLoading, setList, setDataLoading, setData, isFull } = pageState;

    const onAdd = (data) => {
        sideSheet.show(
            data || {
                date: dayjs(),
                hours: "0",
                minutes: "0",
                mode: "DURATION",
            }
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            setListLoading(true);
            try {
                console.log("fetching list");
                const data = await fetchList({ filter: filterState.filter });
                setList(data);
                console.log("fetched list", data);
            } finally {
                setListLoading(false);
            }
        };

        fetchData();
    }, [filterState.filter.listPeriod, setListLoading, setList]);

    useEffect(() => {
        const fetchData = async () => {
            setData({});
            setDataLoading(true);
            try {
                const data = await fetchDetail({
                    filter: filterState.filter,
                    type: filterState.filter.detailPeriod.type,
                    date: filterState.filter.detailPeriod.date,
                });
                setData(data);
            } finally {
                setDataLoading(false);
            }
        };

        fetchData();
    }, [filterState.filter.detailPeriod, setDataLoading, setData]);

    const changeListPeriod = (listPeriod) => {
        let detailPeriod = filterState.filter.detailPeriod;
        if (!isInPeriod(listPeriod.date, detailPeriod)) {
            detailPeriod = initPeriod(detailPeriod.type, listPeriod.date);
        }
        filterState.setFilter({ ...filterState.filter, listPeriod, detailPeriod });
    };
    const changeDetailPeriod = (detailPeriod) => {
        let listPeriod = filterState.filter.listPeriod;
        if (!isInPeriod(detailPeriod.date, listPeriod)) {
            listPeriod = initPeriod(listPeriod.type, detailPeriod.date);
        }
        filterState.setFilter({ ...filterState.filter, listPeriod, detailPeriod });
    };

    const monthId = dayjs(filterState.filter.detailPeriod?.date).month();
    return (
        <>
            <FilterContext.Provider value={filterState}>
                <PageContext.Provider value={pageState}>
                    <MainPage>
                        <ListView>
                            <ListDetailViewSectionLayout loading={state.listLoading}>
                                <ListDetailViewSectionHeader
                                    title="Výkazy"
                                    role="list"
                                    loading={state.listLoading}
                                    compact={!isFull}
                                    actions={[
                                        {
                                            title: "Přidat ...",
                                            icon: <AddOutlined />,
                                            onClick: () => onAdd(),
                                        },
                                    ]}></ListDetailViewSectionHeader>
                                <PeriodField
                                    value={filterState.filter.listPeriod}
                                    onChange={changeListPeriod}
                                    types={["year"]}
                                />
                                {!isFull && (
                                    <Box>
                                        <TimesheetOverview
                                            data={state.data}
                                            period={filterState.filter.detailPeriod}
                                        />
                                    </Box>
                                )}
                                {monthNames.map((m, idx) => {
                                    return (
                                        <TimesheetListItem
                                            key={idx}
                                            data={{
                                                name: m,
                                                hours: state.list?.months?.[idx]?.hours || 0,
                                            }}
                                            loading={state.listLoading}
                                            onClick={() => {
                                                filterState.setFilter({
                                                    ...filterState.filter,
                                                    detailPeriod: initPeriod(
                                                        filterState.filter.detailPeriod.type,
                                                        dayjs(filterState.filter.detailPeriod.date)
                                                            .date(1)
                                                            .month(idx)
                                                    ),
                                                });
                                            }}
                                            selected={monthId === idx}
                                        />
                                    );
                                })}
                            </ListDetailViewSectionLayout>
                        </ListView>
                        {isFull && (
                            <MainView>
                                <ListDetailViewSectionLayout loading={state.dataLoading}>
                                    <ListDetailViewSectionHeader
                                        title={formatPeriod(filterState.filter.detailPeriod)}
                                        titleNode={
                                            <>
                                                <PeriodSelector
                                                    value={filterState.filter.detailPeriod || {}}
                                                    onChange={changeDetailPeriod}
                                                    disabled={state.dataLoading}
                                                    types={["month", "isoWeek", "day"]}
                                                />
                                            </>
                                        }
                                        role="overview"
                                        actions={[
                                            {
                                                title: "Přidat ...",
                                                icon: <AddOutlined />,
                                                onClick: () => onAdd(),
                                            },
                                        ]}
                                    />
                                    <ListDetailViewSectionContent>
                                        <Container maxWidth="xl">
                                            <Box sx={{ py: 4 }}>
                                                <TimesheetOverview
                                                    period={filterState.filter?.detailPeriod}
                                                    data={state.data}
                                                    onChangePeriod={changeDetailPeriod}
                                                />
                                            </Box>
                                        </Container>
                                    </ListDetailViewSectionContent>
                                </ListDetailViewSectionLayout>
                            </MainView>
                        )}
                    </MainPage>
                </PageContext.Provider>
            </FilterContext.Provider>

            <TimesheetModal
                withProject
                open={sideSheet.open}
                data={sideSheet.data}
                hide={sideSheet.hide}
                onSubmit={async (data) => {
                    await storeTimesheet(data);
                    sideSheet.hide();
                    //refreshList();
                    //refreshDetail();
                    showSnackbar("Uloženo");
                }}
            />
            <Snackbar
                open={!!snackbar}
                autoHideDuration={3500}
                onClose={() => showSnackbar(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                message="Uloženo"
            />
            {/* TODO zoom effect? */}
            <Fab
                color="primary"
                aria-label="add"
                onClick={onAdd}
                sx={{ position: "fixed", bottom: 16, right: 16 }}>
                <AddOutlined />
            </Fab>
        </>
    );
};
